import { useCallback, useState } from 'react';

const STORAGE_KEY = 'projection-overrides-net-worth';

/** Bounds mirror the backend clamps (LedgerAnalyticsController). */
const RETURN_RATE_BOUND = 1.0;
const CONTRIBUTION_BOUND = 1_000_000_000;
const MIN_YEARS = 1;
const MAX_YEARS = 100;

/** Withdrawal rate bounds for the FI multiplier input (percent). */
const MIN_WITHDRAWAL_RATE = 0.1;
const MAX_WITHDRAWAL_RATE = 99;
const DEFAULT_WITHDRAWAL_RATE = 4.0;

/** Inflation-rate bounds for the post-FI withdrawal simulation (decimal). */
const MIN_INFLATION_RATE = 0.0;
const MAX_INFLATION_RATE = 0.2;

export interface ProjectionOverrides {
    /** Whether financial-independence mode is enabled (opt-in, default off) */
    fiEnabled: boolean;
    /** User override for return rate (null = use calculated/default) */
    returnRate: number | null;
    /** User override for yearly contribution (null = use calculated/default) */
    contribution: number | null;
    /** User override for yearly debt change, negative = paydown (null = use calculated trend) */
    debtChange: number | null;
    /** User override for projection years (undefined = use server default) */
    years: number | undefined;
    /** Annual expenses for FI target; null = unset, no FI line shown */
    annualExpenses: number | null;
    /** Collection IDs counted toward the FI calculation; null = all included (default) */
    fiIncludedCollectionIds: number[] | null;
    /** Withdrawal rate percent driving the FI multiplier (100 / rate); default 4.0 */
    fiWithdrawalRatePct: number;
    /**
     * FI-specific override for the investable bucket's return rate (decimal);
     * null = auto-derived from the selected collections' history. Separate from
     * the header `returnRate` override — only used in compositional mode.
     */
    fiInvestableReturnRate: number | null;
    /**
     * Whether the post-FI withdrawal simulation is on (opt-in, default off).
     * When on, the projection runs in real terms and, from the year after FI is
     * reached, contributions stop and `annualExpenses` is withdrawn each year.
     */
    fiSimulateWithdrawal: boolean;
    /**
     * Inflation-rate override for the real-terms simulation (decimal, bounded
     * [0, 0.2]); null = use the server default (config.analytics.defaultInflationRate).
     * Only meaningful when `fiSimulateWithdrawal` is on.
     */
    fiInflationRate: number | null;
}

const EMPTY_OVERRIDES: ProjectionOverrides = {
    fiEnabled: false,
    returnRate: null,
    contribution: null,
    debtChange: null,
    years: undefined,
    annualExpenses: null,
    fiIncludedCollectionIds: null,
    fiWithdrawalRatePct: DEFAULT_WITHDRAWAL_RATE,
    fiInvestableReturnRate: null,
    fiSimulateWithdrawal: false,
    fiInflationRate: null,
};

function isBoundedNumber(value: unknown, bound: number): value is number {
    return typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= bound;
}

function clampWithdrawalRate(value: number): number {
    return Math.min(MAX_WITHDRAWAL_RATE, Math.max(MIN_WITHDRAWAL_RATE, value));
}

function readStoredCollectionIds(value: unknown): number[] | null {
    if (!Array.isArray(value)) return null;
    const ids = value.filter(
        (id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0,
    );
    // Empty selection is treated as "all included" (the null default).
    return ids.length > 0 ? ids : null;
}

function readStoredOverrides(): ProjectionOverrides {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return EMPTY_OVERRIDES;

        const parsed: unknown = JSON.parse(stored);
        if (typeof parsed !== 'object' || parsed === null) return EMPTY_OVERRIDES;

        const candidate = parsed as Record<string, unknown>;
        return {
            fiEnabled: candidate.fiEnabled === true,
            returnRate: isBoundedNumber(candidate.returnRate, RETURN_RATE_BOUND)
                ? candidate.returnRate
                : null,
            contribution: isBoundedNumber(candidate.contribution, CONTRIBUTION_BOUND)
                ? candidate.contribution
                : null,
            debtChange: isBoundedNumber(candidate.debtChange, CONTRIBUTION_BOUND)
                ? candidate.debtChange
                : null,
            years: isBoundedNumber(candidate.years, MAX_YEARS) && candidate.years >= MIN_YEARS
                ? Math.round(candidate.years)
                : undefined,
            annualExpenses: isBoundedNumber(candidate.annualExpenses, CONTRIBUTION_BOUND)
                && candidate.annualExpenses > 0
                ? candidate.annualExpenses
                : null,
            fiIncludedCollectionIds: readStoredCollectionIds(candidate.fiIncludedCollectionIds),
            fiWithdrawalRatePct: isBoundedNumber(candidate.fiWithdrawalRatePct, MAX_WITHDRAWAL_RATE)
                && candidate.fiWithdrawalRatePct >= MIN_WITHDRAWAL_RATE
                ? candidate.fiWithdrawalRatePct
                : DEFAULT_WITHDRAWAL_RATE,
            fiInvestableReturnRate: isBoundedNumber(candidate.fiInvestableReturnRate, RETURN_RATE_BOUND)
                ? candidate.fiInvestableReturnRate
                : null,
            fiSimulateWithdrawal: candidate.fiSimulateWithdrawal === true,
            fiInflationRate: isBoundedNumber(candidate.fiInflationRate, MAX_INFLATION_RATE)
                && candidate.fiInflationRate >= MIN_INFLATION_RATE
                ? candidate.fiInflationRate
                : null,
        };
    } catch {
        // localStorage unavailable or corrupted value
        return EMPTY_OVERRIDES;
    }
}

function isDefaultOverrides(overrides: ProjectionOverrides): boolean {
    return (
        overrides.fiEnabled === false
        && overrides.returnRate === null
        && overrides.contribution === null
        && overrides.debtChange === null
        && overrides.years === undefined
        && overrides.annualExpenses === null
        && overrides.fiIncludedCollectionIds === null
        && overrides.fiWithdrawalRatePct === DEFAULT_WITHDRAWAL_RATE
        && overrides.fiInvestableReturnRate === null
        && overrides.fiSimulateWithdrawal === false
        && overrides.fiInflationRate === null
    );
}

function persistOverrides(overrides: ProjectionOverrides): void {
    try {
        if (isDefaultOverrides(overrides)) {
            localStorage.removeItem(STORAGE_KEY);
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
        }
    } catch {
        // localStorage might be unavailable
    }
}

/**
 * Projection override settings (return rate, contribution, years, FI inputs)
 * persisted to localStorage, so tuned values survive navigation and reloads.
 * Resetting clears the stored values and returns to server-calculated ones.
 */
export function useProjectionOverrides() {
    const [overrides, setOverrides] = useState<ProjectionOverrides>(readStoredOverrides);

    const update = useCallback((partial: Partial<ProjectionOverrides>) => {
        setOverrides(prev => {
            const next = { ...prev, ...partial };
            persistOverrides(next);
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setOverrides(EMPTY_OVERRIDES);
        persistOverrides(EMPTY_OVERRIDES);
    }, []);

    // Scoped reset: clears the FI section back to its defaults (expenses,
    // withdrawal rate, included collections, investable rate, and the whole
    // withdrawal simulation) without touching `fiEnabled` (the section stays
    // open) or the header fields. A single `update` keeps persistence consistent.
    const resetFiSettings = useCallback(() => {
        update({
            annualExpenses: null,
            fiWithdrawalRatePct: DEFAULT_WITHDRAWAL_RATE,
            fiIncludedCollectionIds: null,
            fiInvestableReturnRate: null,
            fiSimulateWithdrawal: false,
            fiInflationRate: null,
        });
    }, [update]);

    // Scoped reset: clears only the post-FI withdrawal simulation (toggle off,
    // inflation override cleared), leaving the rest of the FI settings intact.
    const resetWithdrawalSimulation = useCallback(() => {
        update({
            fiSimulateWithdrawal: false,
            fiInflationRate: null,
        });
    }, [update]);

    return {
        ...overrides,
        setFiEnabled: useCallback((fiEnabled: boolean) => update({ fiEnabled }), [update]),
        setReturnRate: useCallback((returnRate: number | null) => update({ returnRate }), [update]),
        setContribution: useCallback((contribution: number | null) => update({ contribution }), [update]),
        setDebtChange: useCallback((debtChange: number | null) => update({ debtChange }), [update]),
        setYears: useCallback((years: number | undefined) => update({ years }), [update]),
        setAnnualExpenses: useCallback((annualExpenses: number | null) => update({ annualExpenses }), [update]),
        setFiIncludedCollectionIds: useCallback((fiIncludedCollectionIds: number[] | null) => {
            // Empty selection is treated as "all included" (null).
            const normalized = fiIncludedCollectionIds && fiIncludedCollectionIds.length > 0
                ? fiIncludedCollectionIds
                : null;
            update({ fiIncludedCollectionIds: normalized });
        }, [update]),
        setFiWithdrawalRatePct: useCallback((fiWithdrawalRatePct: number) => {
            update({ fiWithdrawalRatePct: clampWithdrawalRate(fiWithdrawalRatePct) });
        }, [update]),
        setFiInvestableReturnRate: useCallback((fiInvestableReturnRate: number | null) => {
            update({ fiInvestableReturnRate });
        }, [update]),
        setFiSimulateWithdrawal: useCallback((fiSimulateWithdrawal: boolean) => {
            update({ fiSimulateWithdrawal });
        }, [update]),
        setFiInflationRate: useCallback((fiInflationRate: number | null) => {
            update({ fiInflationRate });
        }, [update]),
        reset,
        resetFiSettings,
        resetWithdrawalSimulation,
    };
}
